import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { CodeBlock, CopyButton } from "@/components/vai/Markdown";
import { errorText, vai } from "@/lib/vai";
import { confirmDialog } from "@/components/vai/Dialogs";

// Clés API de Vanta AI (offres Pro et Max) : créer, voir une fois, supprimer,
// et exemple d'appel (même format que l'API OpenAI).

const when = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "jamais");

export default function ApiKeysPage({ me, onBack, onUpgrade }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null); // clé complète, affichée une seule fois
  const load = useCallback(() => vai.get("/api-keys").then(setData).catch((err) => toast.error(errorText(err))), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await vai.post("/api-keys", { name: name.trim() || "Ma clé" });
      setFresh(r);
      setName("");
      load();
    } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const remove = async (k) => {
    if (!await confirmDialog({ title: `Supprimer la clé « ${k.name} » ?`, text: "Les programmes qui l'utilisent ne pourront plus appeler Vanta AI.", confirmLabel: "Supprimer", danger: true })) return;
    try { await vai.del(`/api-keys/${k.id}`); toast.success("Clé supprimée"); if (fresh?.id === k.id) setFresh(null); load(); } catch (err) { toast.error(errorText(err)); }
  };

  const base = `${window.location.origin}/api/vai/v1`;
  const model = "vector";
  const curl = `curl ${base}/chat/completions \\
  -H "Authorization: Bearer $VANTA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model": "${model}", "messages": [{"role": "user", "content": "Bonjour !"}]}'`;
  const python = `import os
from openai import OpenAI

client = OpenAI(base_url="${base}", api_key=os.environ["VANTA_API_KEY"])
reponse = client.chat.completions.create(
    model="${model}",
    messages=[{"role": "user", "content": "Bonjour !"}],
)
print(reponse.choices[0].message.content)`;

  return (
    <div className="flex-1 overflow-y-auto" data-testid="vai-api-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" />Retour</button>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><KeyRound className="w-6 h-6" />Clés API</h1>
        <p className="text-sm text-white/55 mt-2">Utilise Vanta AI dans tes bots, tes scripts et tes sites. Même format que l'API OpenAI : les bibliothèques existantes marchent en changeant seulement l'adresse.</p>

        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div> : !data.allowed ? (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="font-medium">L'accès API est inclus dans les offres Pro et Max.</p>
            <p className="text-sm text-white/55 mt-1">Tu es sur l'offre {data.plan}.</p>
            <button type="button" onClick={onUpgrade} className="mt-4 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-violet-600 text-sm font-medium hover:brightness-110">Passer à Pro</button>
          </div>
        ) : (
          <>
            {fresh && (
              <div className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4" data-testid="vai-api-fresh">
                <p className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-300" />Copie ta clé maintenant : elle ne sera plus jamais affichée.</p>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2">
                  <code className="flex-1 text-xs font-mono break-all">{fresh.key}</code>
                  <CopyButton text={fresh.key} className="text-xs text-white/70 hover:text-white shrink-0" />
                </div>
                <p className="text-xs text-white/50 mt-2">Garde-la secrète : ne la mets jamais dans un chat, une capture ou un dépôt public. En cas de fuite, supprime-la ici.</p>
              </div>
            )}
            <form onSubmit={create} className="mt-6 flex flex-wrap gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Nom de la clé (ex. Bot Discord)"
                className="flex-1 min-w-[200px] rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/25" data-testid="vai-api-name" />
              <button type="submit" disabled={busy || data.keys.length >= data.max} className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="vai-api-create">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Créer une clé
              </button>
            </form>
            <div className="mt-4 rounded-xl border border-white/10 divide-y divide-white/[0.06]">
              {data.keys.length === 0 && <p className="px-4 py-3 text-sm text-white/40">Aucune clé pour l'instant.</p>}
              {data.keys.map((k) => (
                <div key={k.id} className="flex items-center gap-3 px-4 py-3" data-testid="vai-api-key">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{k.name}</p>
                    <p className="text-xs text-white/45"><code className="font-mono">{k.prefix}</code> · créée le {when(k.created_at)} · utilisée {when(k.last_used_at)}</p>
                  </div>
                  <button type="button" onClick={() => remove(k)} className="p-2 rounded-lg text-red-300/80 hover:text-red-200 hover:bg-red-500/10" aria-label={`Supprimer ${k.name}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 mt-2">{data.keys.length}/{data.max} clés. Les appels comptent dans ton utilisation (Réglages &gt; Utilisation).</p>
          </>
        )}

        <h2 className="text-lg font-semibold mt-10">Exemples</h2>
        <p className="text-sm text-white/55 mt-1">Adresse de base : <code className="font-mono text-white/80">{base}</code> · modèles : {me.models.filter((m) => m.allowed).map((m) => m.id).join(", ")}</p>
        <CodeBlock code={curl} language="bash" />
        <CodeBlock code={python} language="python" />
      </div>
    </div>
  );
}
